import * as Sentry from '@sentry/react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { isDeployRecoverableError, reloadForDeployError } from '@/app/chunk-error-recovery'

type AppErrorBoundaryProps = {
  children: ReactNode
}

type AppErrorBoundaryState = {
  error: Error | null
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  override state: AppErrorBoundaryState = {
    error: null,
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    // 可自动恢复的错误不进入错误页，避免刷新前闪一下错误 UI
    if (isDeployRecoverableError(error)) {
      return { error: null }
    }
    return { error }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // 部署后可刷新恢复的错误（chunk 加载失败、新旧 bundle 运行时不兼容等）
    if (isDeployRecoverableError(error)) {
      reloadForDeployError()
      return
    }

    Sentry.captureException(error, {
      tags: {
        boundary: 'app',
      },
      extra: {
        componentStack: errorInfo.componentStack,
      },
    })
  }

  private handleReload = () => {
    window.location.reload()
  }

  private handleGoHome = () => {
    window.location.assign('/')
  }

  override render() {
    if (!this.state.error) {
      return this.props.children
    }

    return (
      <main className="min-h-screen bg-white text-black flex items-center justify-center px-6">
        <section className="w-full max-w-md rounded-2xl border border-gray-200 p-6 text-center">
          <p className="text-sm font-medium text-gray-500">Application error</p>
          <h1 className="mt-3 text-2xl font-semibold">Something went wrong</h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">
            The page hit an unexpected error. You can reload the page or return home.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-full bg-black px-4 py-2 text-sm font-medium text-white hover:bg-black/80"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-black hover:bg-gray-50"
            >
              Go home
            </button>
          </div>
        </section>
      </main>
    )
  }
}
